import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Form } from "@/components/ui/form";
import {
  DocumentFormGroup,
  QuickChanceFormGroup,
} from "./FormGroups";
import { defaultValues } from "./defaultValues";
import { createFormSchema } from "./formSchema";
import { useCreateQuickTenderMutationHook } from "@/hooks/mutations";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import CreateTenderSideNavMenu from "../Shared/CreateTenderSideNavMenu";
import { CreateTenderFormGroups } from "@/constants/enums";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { HamburgerMenuIcon } from "@radix-ui/react-icons";
import { useBreakpoint } from "@/hooks";
import { FormGroupHeader } from "@/components/shared";
import { ExecutiveSummaryFormGroup } from "../Shared/FormGroups/ExecutiveSummaryFormGroup";
import { Button } from "@/components/ui/button";
import { FormGroupItem } from "../Shared/types";
import { formGroupSchemas } from "./formSchema";
import { TenderType } from "@/graphql/__generated__/graphql";
import { processDocuments, useFormGroupScrollSpy } from "../Shared/helpers";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { CircleLoader } from "@/components/shared/loaders";
import { cn } from "@/lib/utils";
import useUserStore from "@/store/shield";
import { useFetchCustomersLazyQueryHook } from "@/hooks/queries";
import { prefillQuickFormWithMockData } from "./mocks";

function FormComponent() {
  // States
  const [activeFormGroup, setActiveFormGroup] = useState<CreateTenderFormGroups>(CreateTenderFormGroups.DOCUMENTS);
  const [isOpen, setIsOpen] = useState(false);

  // Create refs for each form group card
  const formGroupCardRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Hooks
  const navigate = useNavigate();
  const isMdBreakpoint = useBreakpoint("md");
  const { t } = useTranslation(["translation", "schemas"]);
  const [searchParams] = useSearchParams();
  const aiImportID = searchParams.get("importID");

  // Check if in development mode
  const isDevMode = import.meta.env.VITE_APP_ENV === "dev";

  // Get customers for mock data
  const { fetchCustomers, customers } = useFetchCustomersLazyQueryHook();

  useEffect(() => {
    if (aiImportID) {
      setActiveFormGroup(CreateTenderFormGroups.DESCRIPTION);
    }
    else {
      setActiveFormGroup(CreateTenderFormGroups.DOCUMENTS);
    }
  }, []);

  const FormSchema = useMemo(() => createFormSchema(t, aiImportID), [t, aiImportID]);

  const { createQuickTenderMutation, loading } = useCreateQuickTenderMutationHook();

  const form = useForm<z.infer<typeof FormSchema>>({
    shouldUnregister: false, // Ensure that the form values are not unregistered while navigating between form groups
    resolver: zodResolver(FormSchema),
    defaultValues,
    mode: "onChange"
  });

  // Mock data function for testing
  const handlePrefillMockData = () => {
    fetchCustomers();
    const currentOrgId = useUserStore.getState().orgId;
    prefillQuickFormWithMockData(form, customers, currentOrgId);
  };

  const onInvalid = (errors: unknown) => {
    console.error(errors);
  };

  // Contains the form groups to render and what to validation to trigger on navigating with next button
  const formGroups: FormGroupItem[] = [];

  formGroups.push(
    {
      group: CreateTenderFormGroups.DOCUMENTS,
      components: [<DocumentFormGroup key="document" form={form} />],
      keys: ["document"],
      description: t("newtender.formgroups.document.description"),
      label: t("newtender.navlinks.document"),
    },
    {
      group: CreateTenderFormGroups.QUICK,
      components: [
        <QuickChanceFormGroup
          key="quick"
          form={form}
        />,
      ],
      keys: ["quick", "operationalOrganizationIds"],
      description: t("newtender.formgroups.quick.description"),
      label: t("newtender.navlinks.details"),
      additionalFormGroup: [CreateTenderFormGroups.OPERATIONALORGANIZATION],
    },
    {
      group: CreateTenderFormGroups.EXECUTIVESUMMARY,
      components: [<ExecutiveSummaryFormGroup key="executiveSummary" form={form} />],
      keys: ["executiveSummary"],
      description: t("newtender.formgroups.executivesummary.description"),
      label: t("newtender.navlinks.executivesummary"),
    }
  );

  function onSubmit(data: z.infer<typeof FormSchema>) {
    const { executiveSummary, quick, ...rest } = data;

    // Convert the form data to the correct format for the mutation
    const variables = {
      input: {
        ...rest,
        ...quick,
        description: {
          ...quick.description,
          type: TenderType.QuickOffer
        }
      },
      executiveSummary
    };

    // If the document field exists in the data, we need to handle the documents
    if ("document" in data) {
      const { document } = data;
      const { documents, categoryIndexes } = processDocuments(
        document as Record<string, File[]>,
        {
          publication: "publications",
          serviceDescription: "serviceDescription",
          contract: "contracts",
          otherDocuments: "otherDocuments",
          priceForms: "priceForms"
        }
      );

      // Remove document field from variables as documents will be passed in as separate fields
      delete variables["input"]["document"];

      // Add processed documents
      variables["documents"] = documents;

      // Add category indexes
      Object.entries(categoryIndexes).forEach(([key, value]) => {
        variables[key] = value;
      });
    }

    createQuickTenderMutation({ variables }).then((results) => {
      // navigate to the tender page using result id
      const tenderId = results.data?.createQuickTender.id;
      if (tenderId) {
        form.reset(defaultValues);
        navigate(`/tender/${tenderId}`);
      }
    });
  }

  const handleSubmitOnClick = form.handleSubmit(onSubmit, onInvalid);

  /**
   * Custom useEffect hook.
   * Handles the observer for the active form group.
   */
  useFormGroupScrollSpy(
    formGroups,
    formGroupCardRefs,
    setActiveFormGroup,
    300
  );

  return (
    <div>
      <div className="flex">
        {/* Navigation Menu */}
        <div className="z-50">
          {
            isMdBreakpoint ? (
              <CreateTenderSideNavMenu
                formGroups={formGroups}
                activeFormGroup={activeFormGroup}
                form={form}
                aiPrefilled={false}
                inSheet={true}
                setIsOpen={setIsOpen}
                formGroupSchemas={formGroupSchemas}
              />
            ) :
              (
                <div className="py-3 pl-4 pr-2">
                  <Sheet open={isOpen} onOpenChange={setIsOpen}>
                    <SheetTrigger data-cy="mobile-menu-trigger"><HamburgerMenuIcon className="w-5 h-5" /></SheetTrigger>
                    <SheetContent className="w-[300px] px-0" side="left">
                      <CreateTenderSideNavMenu
                        formGroups={formGroups}
                        activeFormGroup={activeFormGroup}
                        form={form}
                        aiPrefilled={false}
                        inSheet={true}
                        setIsOpen={setIsOpen}
                        formGroupSchemas={formGroupSchemas}
                      />
                    </SheetContent>
                  </Sheet>
                </div>
              )
          }
        </div>

        {/* Form */}
        <div
          id="form-scroll-container"
          className="grow h-screen overflow-y-auto pb-[100px]"
        >
          {/* Form Header */}
          <FormGroupHeader
            title={t("newtender.titlequick")}
            description={t("newtender.description")}
          >
            <>
              {isDevMode && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePrefillMockData}
                  disabled={loading}
                  className="mr-2"
                >
                  Prefill Mock Data
                </Button>
              )}
              <Button
                disabled={loading}
                onClick={handleSubmitOnClick}
                className="gap-2"
              >
                <CircleLoader visible={loading} height={16} width={16} strokeWidth={2} />
                {t("newtender.btncreatequicktender")}
              </Button>
            </>
          </FormGroupHeader>
          <Form {...form}>
            <form className="space-y-6 pb-[300px]">
              {formGroups.map((item, idx) => (
                <React.Fragment key={item.group}>
                  <Card
                    className={cn("py-4", idx === 0 && "mt-4")}
                    id={item.group}
                    ref={(el) => { formGroupCardRefs.current[idx] = el; }}
                  >
                    {/* Card Header */}
                    <div className="px-4">
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg font-semibold">{t(`newtender.formgroups.${item.keys[0].toLowerCase()}.title`)}</h2>
                      </div>
                      <p className="text-sm text-gray-500">{item.description}</p>
                      <Separator className="my-2" />
                    </div>

                    {item.components.map((Component, index) => (
                      <div key={index} className="px-4 pt-2">
                        {Component}
                      </div>
                    ))}
                  </Card>
                </React.Fragment>
              ))}
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default FormComponent;
